/**
 * Deriv API v2 request and response normalization utilities for SmartTrader.
 *
 * v2 renamed several response fields and returns some numeric values as strings.
 * Applied at the WebSocket boundary so consumers receive stable aliases and
 * outbound requests use the canonical v2 schema.
 */

const RESPONSE_ALIASES = {
    underlying_symbol     : 'symbol',
    underlying_symbol_name: 'display_name',
    underlying_symbol_type: 'symbol_type',
    exit_spot             : 'sell_spot',
    exit_tick_time        : 'sell_spot_time',
};

// Numeric fields v2 sometimes returns as strings — coerce after rename (so keys
// are in their consumer-facing alias form).
const NUMERIC_FIELDS = new Set([
    'ask_price',
    'payout',
    'bid_price',
    'buy_price',
    'sell_price',
    'current_spot',
    'profit',
    'sold_for',
    'quote',
    'ask',
    'bid',
    'balance',
    'sell_spot',
    'entry_spot',
    'entry_tick',
    'exit_tick',
    'pip_size',
]);

function toDerivNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}

/**
 * Recursively populate consumer aliases and coerce numeric strings.
 * Pure function — does not mutate the input.
 */
function normalizeV2Response(obj) {
    if (Array.isArray(obj)) return obj.map(normalizeV2Response);

    if (obj !== null && typeof obj === 'object') {
        const out = {};
        Object.keys(obj).forEach((key) => {
            const mappedKey = Object.prototype.hasOwnProperty.call(RESPONSE_ALIASES, key)
                ? RESPONSE_ALIASES[key]
                : key;
            let normalized = normalizeV2Response(obj[key]);
            if (
                NUMERIC_FIELDS.has(mappedKey)
                && (typeof normalized === 'string' || typeof normalized === 'number')
            ) {
                normalized = toDerivNumber(normalized);
            }
            out[mappedKey] = normalized;
        });
        return out;
    }

    return obj;
}

/**
 * Transform an internal request into the canonical v2 wire format.
 * Returns a new object — does not mutate the input.
 */
function transformV2Request(request) {
    if (!request || typeof request !== 'object') return request;

    const out = Object.assign({}, request);

    // top-level symbol → underlying_symbol (proposal, ticks, ticks_history, etc.)
    if (Object.prototype.hasOwnProperty.call(out, 'symbol')) {
        out.underlying_symbol = out.symbol;
        delete out.symbol;
    }

    // nested parameters.symbol → parameters.underlying_symbol (buy-from-parameters)
    if (
        out.parameters !== null
        && typeof out.parameters === 'object'
        && Object.prototype.hasOwnProperty.call(out.parameters, 'symbol')
    ) {
        const params = Object.assign({}, out.parameters);
        params.underlying_symbol = params.symbol;
        delete params.symbol;
        out.parameters = params;
    }

    // Remove fields rejected by the current API.
    delete out.loginid;
    if (Object.prototype.hasOwnProperty.call(out, 'balance') && out.account === 'all') {
        delete out.account;
    }
    if (Object.prototype.hasOwnProperty.call(out, 'ticks_history')) {
        delete out.adjust_start_time;
    }

    // v2 rejects product_type everywhere (proposal, buy parameters, active_symbols);
    delete out.product_type;
    if (
        out.parameters !== null
        && typeof out.parameters === 'object'
        && Object.prototype.hasOwnProperty.call(out.parameters, 'product_type')
    ) {
        const params = Object.assign({}, out.parameters);
        delete params.product_type;
        out.parameters = params;
    }

    // ask_price/price may be returned as string by v2 — coerce on buy
    if (
        Object.prototype.hasOwnProperty.call(out, 'buy')
        && typeof out.price === 'string'
    ) {
        out.price = parseFloat(out.price) || 0;
    }

    return out;
}

module.exports = {
    normalizeV2Response,
    transformV2Request,
};
